import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../config/firebase';

export interface Lead {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country: string;
  tradingExperience: 'Beginner' | 'Intermediate' | 'Advanced';
  interestedBroker: string;
  interestedPlatform: string;
  leadSource: 'Website' | 'Facebook' | 'Google Ads' | 'Instagram' | 'YouTube' | 'Referral' | 'Other';
  status: 'New' | 'Contacted' | 'Qualified' | 'Demo Account' | 'Live Account' | 'Not Interested' | 'Converted';
  priority: 'Low' | 'Medium' | 'High' | 'Urgent';
  notes: string;
  campaignId?: string;
  adSpend?: number;
  conversionValue?: number;
  lastContactDate?: string;
  nextFollowUpDate?: string;
  assignedTo: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

class FirebaseLeadsService {
  private readonly collectionName = 'leads';
  private readonly collection = collection(db, this.collectionName);

  // Get all leads
  async getAllLeads(): Promise<Lead[]> {
    try {
      const querySnapshot = await getDocs(query(this.collection, orderBy('createdAt', 'desc')));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Lead));
    } catch (error) {
      console.error('Failed to get leads:', error);
      return [];
    }
  }

  // Get leads by status
  async getLeadsByStatus(status: Lead['status']): Promise<Lead[]> {
    try {
      const q = query(this.collection, where('status', '==', status), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Lead));
    } catch (error) {
      console.error('Failed to get leads by status:', error);
      return [];
    }
  }

  // Get leads by source
  async getLeadsBySource(source: Lead['leadSource']): Promise<Lead[]> {
    try {
      const q = query(this.collection, where('leadSource', '==', source), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Lead));
    } catch (error) {
      console.error('Failed to get leads by source:', error);
      return [];
    }
  }

  // Get lead by ID
  async getLeadById(id: string): Promise<Lead | null> {
    try {
      const docRef = doc(this.collection, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        } as Lead;
      }
      return null;
    } catch (error) {
      console.error('Failed to get lead by ID:', error);
      return null;
    }
  }

  // Add new lead
  async addLead(leadData: Omit<Lead, 'id' | 'createdAt' | 'updatedAt'>): Promise<Lead> {
    try {
      const newLeadData = {
        ...leadData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const docRef = await addDoc(this.collection, newLeadData);
      
      return {
        id: docRef.id,
        ...newLeadData
      } as Lead;
    } catch (error) {
      console.error('Failed to add lead:', error);
      throw error;
    }
  }

  // Update lead
  async updateLead(id: string, updates: Partial<Omit<Lead, 'id' | 'createdAt' | 'updatedAt'>>): Promise<Lead | null> {
    try {
      const docRef = doc(this.collection, id);
      const updateData = {
        ...updates,
        updatedAt: new Date().toISOString()
      };

      await updateDoc(docRef, updateData);
      
      // Get updated document
      const updatedDoc = await getDoc(docRef);
      if (updatedDoc.exists()) {
        return {
          id: updatedDoc.id,
          ...updatedDoc.data()
        } as Lead;
      }
      return null;
    } catch (error) {
      console.error('Failed to update lead:', error);
      throw error;
    }
  }

  // Delete lead
  async deleteLead(id: string): Promise<boolean> {
    try {
      const docRef = doc(this.collection, id);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error('Failed to delete lead:', error);
      return false;
    }
  }

  // Update lead status
  async updateLeadStatus(id: string, status: Lead['status']): Promise<Lead | null> {
    try {
      const docRef = doc(this.collection, id);
      const updateData = {
        status,
        updatedAt: new Date().toISOString(),
        ...(status === 'Contacted' && { lastContactDate: new Date().toISOString() })
      };

      await updateDoc(docRef, updateData);
      
      const updatedDoc = await getDoc(docRef);
      if (updatedDoc.exists()) {
        return {
          id: updatedDoc.id,
          ...updatedDoc.data()
        } as Lead;
      }
      return null;
    } catch (error) {
      console.error('Failed to update lead status:', error);
      return null;
    }
  }

  // Search leads
  async searchLeads(searchTerm: string): Promise<Lead[]> {
    try {
      const allLeads = await this.getAllLeads();
      const lowerSearchTerm = searchTerm.toLowerCase();
      
      return allLeads.filter(lead =>
        lead.firstName.toLowerCase().includes(lowerSearchTerm) ||
        lead.lastName.toLowerCase().includes(lowerSearchTerm) ||
        lead.email.toLowerCase().includes(lowerSearchTerm) ||
        lead.phone.includes(lowerSearchTerm) ||
        lead.country.toLowerCase().includes(lowerSearchTerm) ||
        lead.interestedBroker.toLowerCase().includes(lowerSearchTerm) ||
        lead.notes.toLowerCase().includes(lowerSearchTerm)
      );
    } catch (error) {
      console.error('Failed to search leads:', error);
      return [];
    }
  }

  // Get leads statistics
  async getLeadsStats(): Promise<{
    total: number;
    new: number;
    contacted: number;
    qualified: number;
    converted: number;
    conversionRate: number;
    totalValue: number;
    avgDealValue: number;
  }> {
    try {
      const allLeads = await this.getAllLeads();
      
      const stats = {
        total: allLeads.length,
        new: allLeads.filter(l => l.status === 'New').length,
        contacted: allLeads.filter(l => l.status === 'Contacted').length,
        qualified: allLeads.filter(l => l.status === 'Qualified').length,
        converted: allLeads.filter(l => l.status === 'Converted').length,
        conversionRate: 0,
        totalValue: 0,
        avgDealValue: 0
      };

      // Calculate conversion rate
      stats.conversionRate = stats.total > 0 ? (stats.converted / stats.total) * 100 : 0;

      // Calculate total value and average deal value
      const convertedLeads = allLeads.filter(l => l.status === 'Converted' && l.conversionValue);
      stats.totalValue = convertedLeads.reduce((sum, lead) => sum + (lead.conversionValue || 0), 0);
      stats.avgDealValue = stats.converted > 0 ? stats.totalValue / stats.converted : 0;

      return stats;
    } catch (error) {
      console.error('Failed to get leads stats:', error);
      return {
        total: 0,
        new: 0,
        contacted: 0,
        qualified: 0,
        converted: 0,
        conversionRate: 0,
        totalValue: 0,
        avgDealValue: 0
      };
    }
  }

  // Real-time listener for leads
  onLeadsChange(callback: (leads: Lead[]) => void): () => void {
    const q = query(this.collection, orderBy('createdAt', 'desc'));
    
    return onSnapshot(q, (querySnapshot) => {
      const leads = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Lead));
      callback(leads);
    }, (error) => {
      console.error('Error listening to leads changes:', error);
    });
  }
}

export const firebaseLeadsService = new FirebaseLeadsService();